import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';
import { CaptchaHandle, CaptchaResult } from './captcha-types';
import { useCaptchaProvider } from './use-captcha-provider';
import { TencentCaptcha } from './TencentCaptcha';

type Props = {
  onSuccess: (result: CaptchaResult) => void;
  onError: (kind: 'network' | 'javascript' | 'closed', error?: unknown) => void;
};

export const Captcha = forwardRef<CaptchaHandle, Props>(
  ({ onSuccess, onError }, ref) => {
    const provider = useCaptchaProvider();
    const [captchaKey, setCaptchaKey] = useState(0);
    const [visible, setVisible] = useState(false);
    const tencentRef = useRef<CaptchaHandle>(null);
    const pendingExecuteRef = useRef(false);
    const reset = useCallback(() => {
      setVisible(false);
      setCaptchaKey((k) => k + 1);
    }, []);

    const runExecute = useCallback((p: 'yandex' | 'tencent') => {
      if (p === 'tencent') tencentRef.current?.execute();
      else setVisible(true);
    }, []);

    // Клик до резолва /captcha-config откладывается и доисполняется на
    // резолве (экран в это время держит captchaPending).
    useEffect(() => {
      if (provider && pendingExecuteRef.current) {
        pendingExecuteRef.current = false;
        runExecute(provider);
      }
    }, [provider, runExecute]);

    useImperativeHandle(ref, () => ({
      execute: () => {
        if (!provider) {
          pendingExecuteRef.current = true;
          return;
        }
        runExecute(provider);
      },
      reset: () => {
        if (provider === 'tencent') tencentRef.current?.reset();
        else reset();
      },
    }));

    // Пока /captcha-config в полёте — не монтируем ничего: до резолва
    // неизвестно, чей скрипт грузить (раньше тут по дефолту монтировался
    // яндекс и его скрипт грузился даже у tencent-юзеров).
    if (!provider) return null;

    if (provider === 'tencent') {
      return (
        <TencentCaptcha
          ref={tencentRef}
          onSuccess={onSuccess}
          onError={onError}
        />
      );
    }

    // Провайдер известен → монтируем сразу: маунт ПРЕДЗАГРУЖАЕТ скрипт в фоне
    // (visible=false, проверка не бежит), а execute() по клику лишь ставит
    // visible=true — клик не платит за скачивание скрипта.
    return (
      <InvisibleSmartCaptcha
        key={`captcha-${captchaKey}`}
        sitekey={import.meta.env.VITE_YANDEX_CAPTCHA_KEY || ''}
        hideShield
        test={Boolean(import.meta.env.VITE_YANDEX_CAPTCHA_TEST || false)}
        visible={visible}
        onChallengeHidden={() => {
          setVisible(false);
          // Fires both on success (after onSuccess already ran) and when the
          // user dismisses an unsolved challenge. In the latter case nothing
          // else notifies the screen, so without this the screen's own
          // "pending" flag would stay stuck forever, permanently disabling
          // the submit control. Harmless post-success: 'closed' only clears
          // an already-cleared pending flag and never touches the result.
          onError('closed');
        }}
        onTokenExpired={reset}
        onNetworkError={() => {
          setVisible(false);
          onError('network');
        }}
        onJavascriptError={(error) => {
          setVisible(false);
          onError('javascript', error);
        }}
        onSuccess={(token) => {
          setVisible(false);
          onSuccess({ provider: 'yandex', token });
        }}
      />
    );
  },
);
Captcha.displayName = 'Captcha';
